import { getFilterObject, createFiltersFromArray } from 'app/dealer/utils';

import { getNormalizedBranch } from '../utils';

import * as ActionTypes from './constants';
import { checkBoxFilters, radioFilters, rangeFilters } from './filters';

export const defaultState = () => {
  return {
    activeBranch: getNormalizedBranch({}),
    branches: [],
    branchesError: null,
    branchesPending: false,

    branchError: null,
    branchPending: false,
    branchCompleted: false,

    invoices: [],
    invoicesCount: 0,
    invoicesError: null,
    invoicesPending: false,

    invoiceExportPending: false,
    invoiceExportError: false,

    zipCheckError: null,
    zipCheckPending: false,
    zipValid: true,
  };
};

export function billing(state = defaultState(), action) {
  const { type, payload, error } = action;

  if (type === ActionTypes.EXPORT_INVOICE_PDF_REQUEST) {
    const { id } = payload;
    const { invoices } = state;
    const idx = invoices.findIndex(invoice => invoice.id === id);
    const activeInvoice = invoices[idx];

    activeInvoice.downloadPending = true;
    invoices[idx] = activeInvoice;

    return {
      ...state,
      invoices: [...invoices],
      invoiceExportError: false,
      invoiceExportPending: true,
    };
  }

  if (type === ActionTypes.EXPORT_INVOICE_PDF_SUCCESS) {
    const { id } = payload;
    const { invoices } = state;
    const idx = invoices.findIndex(invoice => invoice.id === id);
    const activeInvoice = invoices[idx];

    activeInvoice.downloadPending = false;
    invoices[idx] = activeInvoice;

    return {
      ...state,
      invoices: [...invoices],
      invoiceExportError: false,
      invoiceExportPending: false,
    };
  }

  if (type === ActionTypes.EXPORT_INVOICE_PDF_FAILURE) {
    const { id } = payload;
    const { invoices } = state;
    const idx = invoices.findIndex(invoice => invoice.id === id);
    const activeInvoice = invoices[idx];

    activeInvoice.downloadPending = false;
    invoices[idx] = activeInvoice;

    return {
      ...state,
      invoices: [...invoices],
      invoiceExportError: true,
      invoiceExportPending: false,
    };
  }

  if (type === ActionTypes.GET_BILLING_BRANCHES_REQUEST) {
    return {
      ...state,
      branches: [],
      branchesError: null,
      branchesPending: true,
    };
  }

  if (type === ActionTypes.GET_BILLING_BRANCHES_SUCCESS) {
    return {
      ...state,
      branches: payload.map(getNormalizedBranch),
      branchesError: null,
      branchesPending: false,
    };
  }

  if (type === ActionTypes.GET_BILLING_BRANCHES_FAILURE) {
    return {
      ...state,
      branchesError: error,
      branches: [],
      branchesPending: false,
    };
  }

  if (type === ActionTypes.CHECK_BILLING_ZIP_REQUEST) {
    return {
      ...state,
      zipCheckPending: true,
      zipCheckError: null,
      zipValid: true,
    };
  }

  if (type === ActionTypes.CHECK_BILLING_ZIP_SUCCESS) {
    return {
      ...state,
      zipCheckPending: false,
      zipCheckError: null,
      zipValid: true,
    };
  }

  if (type === ActionTypes.CHECK_BILLING_ZIP_ERROR) {
    const { number } = payload;

    return {
      ...state,
      zipCheckPending: false,
      zipCheckError: number,
      zipValid: false,
    };
  }

  if (type === ActionTypes.UPDATE_BRANCH_BILLING_ADDRESS) {
    const newActiveBranch = {
      ...state.activeBranch,
      ...payload,
    };

    return {
      ...state,
      activeBranch: newActiveBranch,
      branchCompleted: false,
    };
  }

  if (type === ActionTypes.RESET_ACTIVE_BRANCH) {
    return {
      ...state,
      activeBranch: getNormalizedBranch({}),
    };
  }

  if (type === ActionTypes.SET_ACTIVE_BRANCH) {
    return {
      ...state,
      activeBranch: payload,
    };
  }

  if (type === ActionTypes.SUBMIT_BRANCH_BILLING_ADDRESS_REQUEST) {
    return {
      ...state,
      branchCompleted: false,
      branchError: null,
      branchPending: true,
    };
  }

  if (type === ActionTypes.SUBMIT_BRANCH_BILLING_ADDRESS_SUCCESS) {
    const { activeBranch, branches } = state;
    const { id } = activeBranch;
    const idx = branches.findIndex(branch => branch.id === id);

    /**
     * since we have found the branch, we should
     * slot it into the existing array.
     */
    branches[idx] = activeBranch;

    return {
      ...state,
      branchCompleted: true,
      branchError: null,
      branches: [...branches],
      branchPending: false,
    };
  }

  if (type === ActionTypes.SUBMIT_BRANCH_BILLING_ADDRESS_FAILURE) {
    return {
      ...state,
      branchCompleted: false,
      branchError: error,
      branchPending: false,
    };
  }

  if (type === ActionTypes.GET_INVOICES_REQUEST) {
    return {
      ...state,
      invoices: [],
      invoicesCount: 0,
      invoicesError: null,
      invoicesPending: true,
    };
  }

  if (type === ActionTypes.GET_INVOICES_SUCCESS) {
    const { invoices, invoicesCount } = payload;

    return {
      ...state,
      invoices,
      invoicesCount,
      invoicesPending: false,
      invoicesError: null,
    };
  }
  if (type === ActionTypes.GET_INVOICES_FAILURE) {
    return {
      ...state,
      invoices: [],
      invoicesCount: 0,
      invoicesError: error,
      invoicesPending: false,
    };
  }

  return state;
}

export const defaultInvoicesFilterState = () => {
  return {
    createdAt: [],
    customFilterApplied: false,
    canceled: [
      getFilterObject({
        category: 'canceled',
        value: false,
      }),
    ],
    dealer: [],
    defaultInitialized: false,
    page: [getFilterObject({ category: 'page', value: 0 })],
    sortBy: [
      getFilterObject({
        category: 'sortBy',
        value: 'createdAt',
        direction: 'desc',
      }),
    ],
  };
};

export function invoicesFilter(state = defaultInvoicesFilterState(), action) {
  const { payload, type } = action;

  if (type === ActionTypes.OVERRIDE_INVOICES_FILTERS) {
    return {
      ...state,
      ...payload,
      customFilterApplied: true,
    };
  }

  if (type === ActionTypes.SET_INVOICES_FILTERS) {
    const filters = Array.isArray(payload) ? payload : [payload];
    const newFilters = createFiltersFromArray(filters, state, {
      checkBox: checkBoxFilters,
      range: rangeFilters,
      radio: radioFilters,
    });

    return {
      ...state,
      ...newFilters,
      customFilterApplied: true,
    };
  }

  if (type === ActionTypes.SET_DEFAULT_INVOICES_FILTERS) {
    const filters = Array.isArray(payload) ? payload : [payload];
    const newFilters = createFiltersFromArray(filters, state, {
      checkBox: checkBoxFilters,
      range: rangeFilters,
      radio: radioFilters,
    });

    return {
      ...state,
      ...newFilters,
      customFilterApplied: true,
      defaultInitialized: true,
    };
  }

  if (type === ActionTypes.RESET_INVOICES_FILTERS) {
    return defaultInvoicesFilterState();
  }

  if (type === ActionTypes.RESET_INVOICES_FILTER_CATEGORY) {
    const newState = { ...state };

    payload.forEach(category => {
      newState[category] = [];
    });
    return { ...newState };
  }

  if (type === ActionTypes.RESET_INVOICES_FILTER_EXCEPT_CATEGORY) {
    const newState = [];

    payload.forEach(category => {
      newState[category] = state[category];
    });

    return {
      ...defaultInvoicesFilterState(),
      ...newState,
      customFilterApplied: true,
    };
  }

  return state;
}
